import { defineStore } from 'pinia';
import { LayoutTypes, MenuTypes } from '~/constants/ui.constants';
import type { Colors, Company, Component, Menu, Page, Sitemap, ThemeDesign, ThemeDesignLayout } from '~/types';

import type { GoogleTag, Theme } from '~/units/website/types';
import type { MenuLink } from '~/ts-shim';
import { CompanyKeys } from '~/units/website/constants';

export const useWebsiteStore = defineStore('website', {
  state: (): {
    company: Company | null;
    layout: string | null;
    theme: Theme | null;

    menus: Menu[];
    layouts: ThemeDesignLayout[];

    themeDesign: ThemeDesign | null;

    companyInternalColors: Colors;

    pages: Page[];

    googleTags: GoogleTag[];
  } => {
    return {
      company: null,

      // @deprecated use theme instead of layout
      layout: null,
      theme: null,

      menus: [],
      layouts: [],

      themeDesign: null,

      companyInternalColors: {},

      pages: [],

      googleTags: [],
    };
  },
  actions: {
    setCompany(data: Company) {
      this.company = data;
      this.companyInternalColors = data?.colors;
      this.setGoogleTags();
    },

    setTheme(theme: Theme | null) {
      this.layout = theme;
      this.theme = theme;
    },

    __setMenus(menus: Menu[]) {
      this.menus = menus;
    },

    __setLayouts(layouts: ThemeDesignLayout[]) {
      this.layouts = layouts;
    },

    __setThemeDesign(themeDesign: ThemeDesign | null) {
      this.themeDesign = themeDesign;
    },

    __setPages(pages: Page[]) {
      this.pages = pages;
    },

    setSitemap(sitemap: Sitemap) {
      this.__setPages(sitemap.pages || []);
      this.__setMenus(sitemap?.menus || []);
      this.__setLayouts(sitemap?.layouts || []);
      this.__setThemeDesign(sitemap?.theme_design || null);
    },

    setGoogleTags(): void {
      const companyKey = this.company?.company_key as string | undefined;
      if (companyKey === CompanyKeys.BYDESIGN_HOMES) {
        this.googleTags = [
          {
            name: 'ads',
            id: 'AW-10990438948',
          },
          {
            name: 'analytic',
            id: 'G-ZW1TCENK7F',
          },
          {
            name: 'neuron-analytic',
            id: 'G-9T8W7D8ZTR',
          },
        ];
      } else if (companyKey === CompanyKeys.BROWNS_ESTATE_AGENTS) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: 'G-P855SPR7F5',
          },
        ];
      } else if (companyKey === CompanyKeys.ALLEN_RESIDENTAL) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: 'G-9XJETG6D9Y',
          },
        ];
      } else if (companyKey === CompanyKeys.GREY_AND_CO) {
        this.googleTags = [
          {
            name: 'grey-and-co-analytic',
            id: 'G-32P22HD6KP',
          },
          {
            name: 'neuron-analytic',
            id: 'G-29MGZSP52B',
          },
        ];
      } else if (companyKey === CompanyKeys.PRICE_ESTATE_AGENTS) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: 'G-62HYVK8MLY',
          },
        ];
      } else if (companyKey === CompanyKeys.BEERCOCKS) {
        this.googleTags = [
          {
            name: 'beercocks-analytic',
            id: 'G-NHED5ZSEGN',
          },
          {
            name: 'neuron-analytic',
            id: 'G-6S73BCTHH3',
          },
        ];
      } else if (companyKey === CompanyKeys.NOEIGHTYSIXESTATEAGENCY) {
        this.googleTags = [
          {
            name: 'noeightysixestateagency-analytic',
            id: 'G-1NK53RPRG0',
          },
          {
            name: 'neuron-analytic',
            id: 'G-FNZFMSQVNL',
          },
        ];
      } else if (companyKey === CompanyKeys.ICEBERG_DIGITAL) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: 'G-D8Q742M3EX',
          },
          {
            name: 'iceberg-digital-1',
            id: 'AW-857350843',
          },
          {
            name: 'iceberg-digital-2',
            id: 'G-0Z5PZRYDKS',
          },
        ];
      } else if (companyKey === CompanyKeys.NICHOLSONSESTATEAGENTS) {
        this.googleTags = [
          {
            name: 'nicholsonsestateagents-analytic',
            id: 'G-98E43F6GX3',
          },
          {
            name: 'nicholsonsestateagents-2-analytic',
            id: 'AW-10979526369',
          },
          {
            name: 'neuron-analytic',
            id: 'G-6RY02MR58L',
          },
        ];
      }
      else if (companyKey === CompanyKeys.KEYS_AND_LEE) {
        this.googleTags = [
          {
            name: 'keys-and-lee-analytic',
            id: 'G-EDRPQ9C9KS',
          },
          {
            name: 'neuron-analytic',
            id: 'G-WKSJJVHJGN',
          },
        ];
      } else if (companyKey === CompanyKeys.MARK_BURGESS) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: "G-GQ70D68V35"
          },
          {
            name: 'mark-burgess-analytic',
            id: "UA-109720510-1"
          }
        ];
      }

      else if (companyKey === CompanyKeys.KINETIC) {
        this.googleTags = [
          {
            name: 'kinetic-analytic',
            id: "GTM-5586S9C"
          },
          {
            name: 'neuron-analytic',
            id: "G-E25PMGFMZ9"
          }
        ];
      }
      
      else if (companyKey === CompanyKeys.COPE_AND_CO) {
        this.googleTags = [
          {
            name: 'cope-and-co-analytic',
            id: "G-EFSNY1PE34"
          },
          {
            name: 'cope-and-co-analytic-2',
            id: "G-YVMBL8MRMM"
          },
          {
            name: 'cope-and-co-analytic-3',
            id: "GTM-5JH8ZX3H"
          },
          {
            name: 'neuron-analytic',
            id: "G-BZQVGL8FCD"
          }
        ];
      }
      else if (companyKey === CompanyKeys.HARRISON_HOMES) {
        this.googleTags = [
          {
            name: 'harrisonshomes-analytic',
            id: 'G-9KX1QLS05H'
          },
          {
            name: 'neuron-analytic',
            id: 'G-ZEMNQZH2K7'
          },
        ];
      }
      
      else if (companyKey === CompanyKeys.RODERICK_THOMAS) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: "G-1JEMEY5GW6"
          }
        ];
      }
      else if (companyKey === CompanyKeys.TOWN_AND_CITY_HOMES) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: "G-LWMT30B6GC"
          }
        ];
      }
      else if (companyKey === CompanyKeys.JACKSONS_PROPERTY) {
        this.googleTags = [
        {
          name: 'neuron-analytic',
          id: 'G-D6V38S4KPJ'
        }
        ];
      }
      else if (companyKey === CompanyKeys.KEYSTONE_PMC) {
        this.googleTags = [
          {
            name: 'keystonepmc-analytic',
            id: 'G-VNSHS3E70Y'
          }
        ];
      }
      else if (this.companyKey === CompanyKeys.HARRIS_AND_WOOD) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: 'G-76932H1K7W'
          }
        ];
      }
      /*
      else if (this.companyKey === CompanyKeys.TOWN_AND_CITY_HOMES) {
        this.googleTags = [
          
        ];
      }
      */
      /*
      else if (this.companyKey === CompanyKeys.BRIAN_MCG) {
        this.googleTags = [
          {
            name: 'neuron-analytic',
            id: "G-GQ70D68V35"
          },
          {
            name: 'mark-burgess-analytic',
            id: "UA-109720510-1"
          }
        ];
      }
      */
      /*
      else if (this.companyKey === CompanyKeys.ROCHFORD_STOKES) {
        this.googleTags = [
          {
          },
        ];
      }
        */
      
      /* else if (this.companyKey === CompanyKeys.ESTATE_AGENCY_X) {
        this.googleTags = [
        ];
      } */
     
    /*
    else if (this.companyKey === CompanyKeys.PROPERTY_CONNECTIONS) {
        this.googleTags = [
        ];
      }
    */
    /*
    else if (this.companyKey === CompanyKeys.LLOYDS_ESTATES) {
        this.googleTags = [
        ];
      }
    */
   /*
    else if (this.companyKey === CompanyKeys.THE_PROPERTY_CLOUD) {
        this.googleTags = [
        ];
      }
    */
   /*
   else if (this.companyKey === CompanyKeys.PENDLEHILL_PROPERTIES) {
        this.googleTags = [
        ];
      }
  else if (this.companyKey === CompanyKeys.HARBOR_PROPERTY) {
    this.googleTags = [
    ];
  }
  else if (this.companyKey === CompanyKeys.CANTELL_AND_CO) {
    this.googleTags = [
    ];
  }
  */
  },

    calculateMaxRowHeight(componentRef, className) {
      const children = componentRef.children;
      let maxHeight = 0;
      for (let child of children) {
        const featureTextElement = (() => {
          const element = child.getElementsByClassName(className);
          if (element && element[0]) {
            return element[0];
          }
          return null;
        })();
        if (featureTextElement) {
          const height = featureTextElement.scrollHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      }
      return maxHeight;
    },
  },
  getters: {
    currentPage(): Page | undefined | null {
      const route = useRoute();
      const pages = useState('pages').value;
      if (pages && Array.isArray(pages)) {
        if (route.params.personalised_page_id) {
          return pages.find((page: any) => page._id === route.params.personalised_page_id);
        }
        const page = pages.find((page: any) => page._id === route.meta.pageId);
        if (page && !route.meta.personalisedPageId) {
          return page;
        } else if (page && page.personalised_pages) {
          return page.personalised_pages.find((pPage: any) => pPage._id === route.meta.personalisedPageId);
        }
        return page;
      }
      return null;
    },

    headerMenuItems(state): MenuLink[] {
      return state.menus.find((menu) => menu.type === MenuTypes.HEADER)?.items || [];
    },

    footerMenuItems(state) {
      return state.menus.find((menu) => menu.type === MenuTypes.FOOTER)?.items || [];
    },

    headerLayout(state): ThemeDesignLayout | null | undefined {
      if (state.layouts) {
        return state.layouts.find((layout) => layout.layout.name === LayoutTypes.HEADER);
      }
      return null;
    },

    headerMenuComponents(): Component[] {
      if (this.currentPage && this.currentPage.header && this.currentPage.header.length) {
        return this.currentPage.header;
      }
      if (this.headerLayout && this.headerLayout.components) {
        return this.headerLayout.components;
      }
      return [];
    },

    propertiesConfig() {
      if (this.theme === 'theme3') {
        const defaultValues = {
          isSeparatedPriceSelections: true,
          backgroundColor: '',
          advancedSearchPopupStyle: 'background-color: var(--isabelline); top: 328px;',
          advancedSearchMobilePopupBackgroundClass: '!tw-bg-[var(--isabelline)]',
        };
        if (this.company?.company_key === CompanyKeys.BROWNS_ESTATE_AGENTS) {
          return defaultValues;
        } else if (this.company?.company_key === CompanyKeys.NEIL_KING) {
          return {
            ...defaultValues,
            advancedSearchPopupStyle: 'background-color: var(--isabelline); top: 368px;',
          };
        }
        return defaultValues;
      }

      return {
        isSeparatedPriceSelections: false,
        backgroundColor: 'background-color: var(--white);',
        advancedSearchPopupStyle: 'background-color: var(--white);',
        advancedSearchMobilePopupBackgroundClass: '',
      };
    },

    companyKey(state) {
      return state.company?.company_key;
    },

    cssVariables(state) {
      const cssVariables: { [key: string]: string | number | null | undefined | boolean } = {};
      if (this.colors) {
        Object.keys(this.colors).forEach((key) => {
          cssVariables[`--${key}`] = this.colors[key];
        });
      }

      const colorKeys = ['primary_color', 'secondary_color', 'tertiary_color'];
      colorKeys.forEach((colorKey) => {
        if (state.company && state.company[colorKey]) {
          cssVariables[`--${colorKey}`] = state?.company[colorKey];
        }
      });

      return {
        ...cssVariables,

        // no need to have these variables as in top, they already injected to css variables object
        '--akya-text-color': this.colors['akya-text-color'],
        '--akya-background-color': this.colors['akya-background-color'],
        '--akya-confirm-button-text-color': this.colors['akya-confirm-button-text-color'],
        '--akya-confirm-button-background-color': this.colors['akya-confirm-button-background-color'],
      };
    },

    sidebarComponents(): Component[] {
      if (this.currentPage && this.currentPage.sidebar) {
        return this.currentPage.sidebar;
      }
      return [];
    },

    googleTagsScripts(state) {
      return state.googleTags.map((tag) => {
        return {
          key: tag.name,
          type: 'text/javascript',
          src: `https://www.googletagmanager.com/gtag/js?id=${tag.id}`,
          async: !tag.sync,
        };
      });
    },
    colors(state) {
      const colors = state.companyInternalColors;
      try {
        if (colors) {
          return {
            ...colors,
          };
        }
      } catch (e) {}
      return {};
    },

    isOldStructure(state) {
      return ['theme2', 'theme3'].includes(state.theme);
    },
  },
});
